import { Component, ElementRef, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { formatDate } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AppRelease } from '../../models/app-release';
import { renderMarkdown, resolveBodyImages, splitSlides } from '../../markdown';

/** What the slideshow needs. A plain notification has no version/date; a release has both. */
export type SlideshowContent = Pick<AppRelease, 'title' | 'body' | 'bodyImages'> & Partial<AppRelease>;

export interface ReleaseNotesModalData {
  release: SlideshowContent;
  /** Shows "Update now" (reload) instead of a plain Close. */
  updatePending: boolean;
  /** Composer preview of an unsent draft — no update button, ever. */
  preview?: boolean;
  /** Open filling the browser window (the viewer can toggle it either way). */
  fullscreen?: boolean;
}

/** Dialog-level class; styles live in styles.scss because the dialog is appended to body. */
export const FULLSCREEN_DIALOG_CLASS = 'modal-release-notes-fullscreen';

/**
 * Markdown as a slideshow: one slide per `---`-separated block, images and
 * video resolved from signed URLs. Used for release notes AND for any plain
 * notification whose body has more than one slide (NotificationBodyService).
 *
 * Deliberately does NOT inject AppVersionService: ModalService imports this
 * component (to open it) and AppVersionService injects ModalService, so a
 * dependency here would close an import cycle. "Update now" is a reload;
 * the caller decides whether that button exists via `updatePending`.
 */
@Component({
  selector: 'app-release-notes',
  templateUrl: './release-notes.component.html',
  styleUrls: ['./release-notes.component.scss'],
  // The slides are [innerHTML]; emulated encapsulation can't style them.
  encapsulation: ViewEncapsulation.None,
})
export class ReleaseNotesComponent implements OnInit {
  release!: SlideshowContent;
  updatePending = false;
  preview = false;

  fullscreen = false;

  slides: SafeHtml[] = [];
  page = 0;
  /** "Preview · Version 1.0.126 · 4 Sep 2026" — whichever parts apply. */
  meta = '';

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  /** Arrow keys step the slides — for presenting without reaching for the mouse. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight' && this.page < this.slides.length - 1) {
      this.page += 1;
      event.preventDefault();
    } else if (event.key === 'ArrowLeft' && this.page > 0) {
      this.page -= 1;
      event.preventDefault();
    }
  }

  ngOnInit(): void {
    const data = (this.config.data || {}) as ReleaseNotesModalData;

    this.release = data.release;
    this.updatePending = data.updatePending === true && !data.preview;
    this.preview = data.preview === true;
    this.fullscreen = data.fullscreen === true;

    this.renderSlides();

    this.meta = [
      this.preview ? 'Preview' : null,
      this.release?.version ? `Version ${this.release.version}` : null,
      this.release?.publishedAt ? formatDate(this.release.publishedAt, 'd MMM y', 'en-US') : null,
    ]
      .filter((p) => !!p)
      .join(' · ');
  }

  goTo(index: number): void {
    if (index < 0 || index > this.slides.length - 1) return;
    this.page = index;
  }

  prev(): void {
    this.goTo(this.page - 1);
  }

  next(): void {
    this.goTo(this.page + 1);
  }

  get hasPrev(): boolean {
    return this.page > 0;
  }

  get hasNext(): boolean {
    return this.page < this.slides.length - 1;
  }

  /**
   * Toggle at runtime, not just at open: a release sent "normal" can still be
   * blown up for a screen-share. The class goes on the PrimeNG dialog element
   * (this component's ancestor), where the global stylesheet sizes it.
   */
  toggleFullscreen(): void {
    this.fullscreen = !this.fullscreen;

    const dialog = this.elementRef.nativeElement.closest('.p-dialog');
    if (dialog) dialog.classList.toggle(FULLSCREEN_DIALOG_CLASS, this.fullscreen);

    // Videos autoplay in fullscreen (the slide IS the demo), controls-only otherwise.
    this.renderSlides();
  }

  private renderSlides(): void {
    const body = resolveBodyImages(this.release?.body || '', this.release?.bodyImages, {
      autoplayVideo: this.fullscreen,
    });
    const slides = splitSlides(body);

    this.slides = (slides.length ? slides : ['*Nothing to show.*']).map((md) =>
      this.sanitizer.bypassSecurityTrustHtml(renderMarkdown(md))
    );

    if (this.page > this.slides.length - 1) this.page = 0;
  }

  onClose(): void {
    this.ref.close('close');
  }

  onUpdateNow(): void {
    this.ref.close('update');
    document.location.reload();
  }
}
